import { useMutation, useQueryClient } from '@tanstack/react-query'

/**
 * Envelope the `joinWithCode` callable may resolve with. The callable lives in
 * the shared functions backend (same family as `createGroup`); its exact shape
 * isn't in this repo, so we read it defensively: an explicit ERROR status (or a
 * thrown error) is a failure, anything else is treated as success.
 */
export interface JoinHuntResponse {
  status?: string
  errorCode?: string
  message?: string
  /** Present on a SUCCESS envelope; 'ended' is what separates a read-only view from a real join. */
  huntState?: string
}

/** Hunt join codes are NNNLLL — 3 digits then 3 letters (see IHuntJoinCodeGenerator). */
const HUNT_CODE = /^[0-9]{3}[A-Za-z]{3}$/

export function normalizeJoinCode(raw: string): string {
  return raw.trim().toUpperCase()
}

export function isValidJoinCodeFormat(raw: string): boolean {
  return HUNT_CODE.test(normalizeJoinCode(raw))
}

/**
 * True when the callable's envelope indicates failure.
 *
 * Two signals, because the envelope carries both and either one alone is
 * incomplete. An explicit `status: "ERROR"` is the documented refusal. A
 * present `errorCode` is the same thing said the other way round — the server
 * never sets one on a response that succeeded — and catching it means a
 * refusal whose `status` is absent, misspelled, or renamed still reads as a
 * failure rather than silently as a join.
 *
 * Deliberately still lenient about a missing `status` with no `errorCode`:
 * that shape has never been observed, and treating it as failure would turn a
 * join that actually happened into a retry prompt. Hunt redemption goes
 * further and classifies the whole envelope — see `joinOutcome` — but this
 * predicate is also what the team-join path uses, so it stays the narrow
 * question its name asks.
 */
export function isJoinFailure(data: JoinHuntResponse | undefined | null): boolean {
  if (data?.status?.trim().toUpperCase() === 'ERROR') {
    return true
  }
  return (data?.errorCode?.trim() ?? '') !== ''
}

/** What actually happened, as far as the user is concerned. */
export type JoinOutcome = 'joined' | 'viewed' | 'expired' | 'ambiguous' | 'failed'

/**
 * Classifies the callable's envelope.
 *
 * `SUCCESS` alone is not enough: the server returns it both for a real join and
 * for opening a finished hunt read-only, and treating the second as a join is
 * why this page told users a hunt would appear in their list when nothing had
 * been written. A response with no `huntState` comes from an older deployed
 * function and keeps the previous behaviour.
 */
export function joinOutcome(data: JoinHuntResponse | undefined | null): JoinOutcome {
  if (data?.status?.trim().toUpperCase() === 'SUCCESS') {
    return data.huntState === 'ended' ? 'viewed' : 'joined'
  }
  switch (data?.errorCode?.trim().toUpperCase()) {
    case 'HUNT_NOT_ACTIVE':
      return 'expired'
    case 'CODE_AMBIGUOUS':
      return 'ambiguous'
    default:
      return 'failed'
  }
}

/**
 * The message shown when the mutation throws — for every outcome except
 * `joined` and `viewed`, which resolve instead of throwing.
 *
 * `expired` and `ambiguous` are worth distinguishing from a plain failure:
 * the code itself was valid, so telling the user to check it is actively
 * misleading. `expired` means the code is past its 30-day view window, or
 * belongs to a hunt plan whose date has passed, and will never work again.
 * `ambiguous` means the code currently matches two live hunts at once,
 * which the server refuses to guess between. Everything else — an unknown
 * code, a typo, no permission, a network failure — is indistinguishable to
 * the user and shares one message.
 *
 * These English strings are for logs only. What the user reads is chosen by
 * JoinHuntModal from the error's `code` (see {@link JoinHuntFailure}), in the
 * UI language.
 */
export function joinFailureMessage(outcome: JoinOutcome): string {
  switch (outcome) {
    case 'expired':
      return 'That hunt is no longer available.'
    case 'ambiguous':
      return 'Something went wrong with that code. Please try again later.'
    default:
      return 'That hunt code didn’t work. Check it and try again.'
  }
}

/** A failed join, carrying the reason for JoinHuntModal to translate. */
export class JoinHuntFailure extends Error {
  readonly code: 'failed' | 'expired' | 'ambiguous'
  constructor(code: 'failed' | 'expired' | 'ambiguous') {
    super(joinFailureMessage(code))
    this.name = 'JoinHuntFailure'
    this.code = code
  }
}

/**
 * Join a hunt by its code via the `joinWithCode` Firebase callable
 * (europe-west1) — the documented mechanism for hunt codes (it routes by the
 * code's numeric prefix). Mirrors `useCreateGroup`'s direct-callable pattern;
 * everything else goes through the .NET API. On success the hunt lists are
 * refreshed so a real join surfaces in them.
 *
 * A code for a hunt that has already ended, but is still inside its 30-day
 * view window, also resolves rather than throwing: the server writes
 * nothing and hands back a read-only view, so the mutation resolves
 * `'viewed'` instead of `'joined'` and the caller decides what to tell the
 * user. `HUNT_NOT_ACTIVE` means the code has actually expired — past that
 * window, or a hunt plan whose date has passed — and "check the code and try
 * again" is still wrong there: the code is correct and will never work
 * again, which is why that case gets its own message (`joinFailureMessage`).
 */
export function useJoinHuntByCode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rawCode: string): Promise<JoinOutcome> => {
      const code = normalizeJoinCode(rawCode)
      const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([
        import('firebase/app'),
        import('firebase/functions'),
      ])
      const fns = getFunctions(getApp(), 'europe-west1')
      // The callable validates a `joinCode` field (shared with group
      // join-by-code, which sends the same). Sending it as `code` made the
      // field arrive empty, so every hunt join failed with the raw
      // "joinCode: Required".
      const call = httpsCallable<{ joinCode: string }, JoinHuntResponse>(fns, 'joinWithCode')
      const outcome = await call({ joinCode: code })
        .then((res) => joinOutcome(res.data))
        .catch((e) => {
          // Unknown/rejected code, permission, or network. Logged for
          // diagnostics; the raw callable error is never shown to the user.
          //
          // A throw is never a classified refusal: every refusal path in
          // joinWithCode returns an envelope, and the only HttpsErrors the
          // callable can raise are constructed with a code and a message and
          // no `details`, so there is no errorCode to recover from one. A
          // throw here means the call itself did not complete.
          console.error('joinWithCode failed', e)
          return 'failed' as const
        })
      if (outcome === 'failed' || outcome === 'expired' || outcome === 'ambiguous') {
        throw new JoinHuntFailure(outcome)
      }
      return outcome
    },
    onSuccess: () => {
      // Refresh so a real join surfaces in the list. A "viewed" resolution
      // (an ended hunt inside its view window) changed nothing server-side,
      // so this just re-fetches identical data — harmless, not worth
      // branching on.
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'hunts'] })
    },
  })
}
